import React, { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import MarketingNavbar from "@/components/marketing/MarketingNavbar";
import PublicFooter from "@/components/marketing/PublicFooter";
import { initMetaTracking, trackPageView } from "@/lib/metaTracking";
import { captureUtmFromUrl } from "@/lib/utmCapture";

/**
 * Shared layout for public/marketing pages.
 * Sticky navbar + Outlet (page content) + dark enterprise footer.
 * Scrolls to top on route change so each page starts clean.
 *
 * IMPORTANT — Theme isolation:
 * Le marketing page usano il loro trattamento di brand
 * (Pearl Grey / Gold / Navy / Teal) con hero Navy + hard-coded
 * bg-white al layout root. L'attributo `data-executive-dark`
 * (usato dall'app shell interna) causa leakage di regole CSS
 * che invertono il testo bianco sull'hero Navy. Quindi, mentre
 * il layout marketing è montato, rimuoviamo l'attributo e lo
 * ripristiniamo (se era attivo) all'unmount.
 */
export default function MarketingLayout() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Iter 10R — bootstrap Meta Pixel (env-gated) + cattura UTM al primo load
  // marketing. Entrambi no-op se le env vars non sono valorizzate; nessun
  // errore lato UX, nessun secret nel bundle frontend.
  useEffect(() => {
    try {
      captureUtmFromUrl();
      initMetaTracking();
    } catch (_) {
      /* silent — mai bloccare la landing */
    }
  }, []);

  // PageView su ogni navigazione client-side.
  useEffect(() => {
    try {
      trackPageView({ page_path: location.pathname });
    } catch (_) { /* silent */ }
  }, [location.pathname]);

  useEffect(() => {
    // Isolate marketing from executive-dark admin theme.
    const root = document.documentElement;
    const wasOn = root.hasAttribute("data-executive-dark");
    if (wasOn) {
      root.removeAttribute("data-executive-dark");
    }
    return () => {
      if (wasOn) {
        root.setAttribute("data-executive-dark", "true");
      }
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col text-[#0F172A] antialiased">
      <MarketingNavbar />
      <main className="flex-1 bg-white">
        <Outlet />
      </main>
      <PublicFooter />
    </div>
  );
}
